import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, Phone, Mail, Check, RefreshCw, Loader2, AlertCircle, ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const ConfirmAppointmentPage = () => {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(true);
  const [sendReminder, setSendReminder] = useState(true);
  const [rescheduleData, setRescheduleData] = useState({
    confirmed_date: '',
    confirmed_time: '',
    confirmed_stylist: '',
    notes: ''
  });

  const stylists = [
    'Emma B', 
    'Charlotte', 
    'Emma S',
    'Lorraine'
  ];

  useEffect(() => {
    const fetchAppointment = async () => {
      try {
        const response = await fetch(`${API_URL}/api/appointments/details/${token}`);
        if (!response.ok) {
          throw new Error('Appointment not found');
        }
        const data = await response.json();
        setAppointment(data);
        
        if (data.status === 'confirmed' || data.status === 'rescheduled') {
          setConfirmed(true);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointment();
  }, [token]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmed: true,
          send_confirmation_email: sendConfirmationEmail,
          send_reminder: sendReminder,
          notes: ''
        })
      });

      const data = await response.json();
      if (data.success) {
        const emailMsg = sendConfirmationEmail ? ' Customer has been notified.' : ' No email sent to customer.';
        toast.success('Appointment confirmed!' + emailMsg);
        setConfirmed(true);
      } else {
        toast.error('Failed to confirm appointment');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this appointment? This cannot be undone.')) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/cancel/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Appointment cancelled.');
        setCancelled(true);
      } else {
        toast.error('Failed to cancel appointment');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    
    if (!rescheduleData.confirmed_date || !rescheduleData.confirmed_time) {
      toast.error('Please select a new date and time');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/appointments/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmed: false,
          confirmed_date: rescheduleData.confirmed_date,
          confirmed_time: rescheduleData.confirmed_time,
          confirmed_stylist: rescheduleData.confirmed_stylist || appointment?.stylist,
          send_confirmation_email: sendConfirmationEmail,
          send_reminder: sendReminder,
          notes: rescheduleData.notes
        })
      });

      const data = await response.json();
      if (data.success) {
        const emailMsg = sendConfirmationEmail ? ' Customer has been notified.' : ' No email sent to customer.';
        toast.success('Appointment rescheduled!' + emailMsg);
        setConfirmed(true);
      } else {
        toast.error('Failed to reschedule appointment');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate time slots
  const timeSlots = [];
  for (let hour = 8; hour <= 17; hour++) {
    for (let min = 0; min < 60; min += 30) {
      if (hour === 17 && min > 0) break;
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-GB', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      timeSlots.push({ value: time, label: displayTime });
    }
  }

  // Get min date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 size={48} className="text-isalonuk-accent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-zinc-900 border-white/10">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <h1 className="font-heading text-2xl text-white mb-2">Appointment Not Found</h1>
            <p className="text-white/60 font-sans mb-6">
              This appointment link may have expired or the appointment has already been processed.
            </p>
            <Link to="/">
              <Button className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full">
                <ArrowLeft size={18} className="mr-2" />
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-12 px-6" data-testid="confirm-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="iSalon UK" className="h-12 mx-auto mb-6" />
          </Link>
          <h1 className="font-heading text-3xl text-white mb-2">
            {cancelled ? 'Appointment Cancelled' : confirmed ? 'Appointment Processed' : 'Appointment Request'}
          </h1>
          <p className="text-white/60 font-sans">
            {cancelled
              ? 'This appointment has been cancelled.'
              : confirmed 
                ? 'This appointment has been confirmed.'
                : 'Review and confirm this appointment request'}
          </p>
        </div>

        {/* Cancelled Message */}
        {cancelled && (
          <Card className="bg-zinc-900 border-white/10 mb-6">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X size={32} className="text-red-500" />
              </div>
              <h2 className="font-heading text-xl text-white mb-2">Appointment Cancelled</h2>
              <p className="text-white/60 font-sans mb-6">
                This appointment has been cancelled. No confirmation email has been sent to the customer.
              </p>
              <Link to="/">
                <Button className="bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-medium rounded-full">
                  <ArrowLeft size={18} className="mr-2" />
                  Back to Home
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Appointment Details Card */}
        {!cancelled && (
        <Card className="bg-zinc-900 border-white/10 mb-6" data-testid="appointment-details">
          <CardContent className="p-6 md:p-8">
            <div className="grid gap-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-isalonuk-accent/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-isalonuk-accent" />
                </div>
                <div>
                  <p className="text-white/50 font-sans text-sm">Customer</p>
                  <p className="text-white font-sans text-lg font-medium">{appointment.customer_name}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-black/50 rounded-xl">
                  <Mail size={20} className="text-isalonuk-accent" />
                  <div>
                    <p className="text-white/50 font-sans text-xs">Email</p>
                    <a href={`mailto:${appointment.customer_email}`} className="text-white font-sans text-sm hover:text-isalonuk-accent">
                      {appointment.customer_email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-black/50 rounded-xl">
                  <Phone size={20} className="text-isalonuk-accent" />
                  <div>
                    <p className="text-white/50 font-sans text-xs">Phone</p>
                    <a href={`tel:${appointment.customer_phone}`} className="text-white font-sans text-sm hover:text-isalonuk-accent">
                      {appointment.customer_phone}
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <h3 className="text-isalonuk-accent font-sans font-semibold text-sm uppercase tracking-wider mb-4">
                  Requested Appointment
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-4 bg-isalonuk-accent/10 rounded-xl border border-isalonuk-accent/30">
                    <Calendar size={20} className="text-isalonuk-accent" />
                    <div>
                      <p className="text-white/50 font-sans text-xs">Date</p>
                      <p className="text-white font-sans font-medium">{appointment.preferred_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-isalonuk-accent/10 rounded-xl border border-isalonuk-accent/30">
                    <Clock size={20} className="text-isalonuk-accent" />
                    <div>
                      <p className="text-white/50 font-sans text-xs">Time</p>
                      <p className="text-white font-sans font-medium">{appointment.preferred_time}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-isalonuk-accent/10 rounded-xl border border-isalonuk-accent/30">
                    <User size={20} className="text-isalonuk-accent" />
                    <div>
                      <p className="text-white/50 font-sans text-xs">Stylist</p>
                      <p className="text-white font-sans font-medium">{appointment.stylist}</p>
                    </div>
                  </div>
                </div>
              </div>

              {appointment.service && (
                <div className="p-4 bg-black/50 rounded-xl">
                  <p className="text-white/50 font-sans text-xs mb-1">Service Requested</p>
                  <p className="text-white font-sans">{appointment.service}</p>
                </div>
              )}

              {appointment.message && (
                <div className="p-4 bg-black/50 rounded-xl">
                  <p className="text-white/50 font-sans text-xs mb-1">Customer Message</p>
                  <p className="text-white font-sans">{appointment.message}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        )}

        {/* Action Buttons */}
        {!confirmed && !cancelled && (
          <>
            {/* Email Options */}
            <Card className="bg-zinc-900 border-white/10 mb-4">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg text-white mb-4">Email Options</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-white font-sans">Send confirmation email to customer</Label>
                      <p className="text-white/50 text-sm">Customer will receive an email confirming their appointment</p>
                    </div>
                    <Switch 
                      checked={sendConfirmationEmail} 
                      onCheckedChange={setSendConfirmationEmail}
                      data-testid="switch-send-email"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-white font-sans">Send 24-hour reminder</Label>
                      <p className="text-white/50 text-sm">Customer will receive a reminder 24 hours before appointment</p>
                    </div>
                    <Switch 
                      checked={sendReminder} 
                      onCheckedChange={setSendReminder}
                      data-testid="switch-send-reminder"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {!showReschedule ? (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={handleConfirm}
                    disabled={isSubmitting}
                    className="flex-1 bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full py-6 text-lg"
                    data-testid="btn-confirm"
                  >
                    {isSubmitting ? (
                      <Loader2 size={20} className="mr-2 animate-spin" />
                    ) : (
                      <Check size={20} className="mr-2" />
                    )}
                    Confirm Appointment
                  </Button>
                  <Button
                    onClick={() => setShowReschedule(true)}
                    variant="outline"
                    className="flex-1 border-white/30 text-white hover:bg-white/10 font-sans font-medium rounded-full py-6 text-lg"
                    data-testid="btn-reschedule"
                  >
                    <RefreshCw size={20} className="mr-2" />
                    Reschedule
                  </Button>
                </div>
                <Button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  variant="outline"
                  className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500 font-sans font-medium rounded-full py-4"
                  data-testid="btn-cancel"
                >
                  <X size={18} className="mr-2" />
                  Cancel Appointment
                </Button>
              </div>
            ) : (
              <Card className="bg-zinc-900 border-white/10">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl text-white mb-4">Reschedule Appointment</h3>
                  <form onSubmit={handleReschedule} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white font-sans">New Date *</Label>
                        <Input
                          type="date"
                          min={minDate}
                          value={rescheduleData.confirmed_date}
                          onChange={(e) => setRescheduleData(prev => ({ ...prev, confirmed_date: e.target.value }))}
                          className="bg-black border-white/20 text-white rounded-xl [color-scheme:dark]"
                          required
                          data-testid="input-new-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white font-sans">New Time *</Label>
                        <select
                          value={rescheduleData.confirmed_time}
                          onChange={(e) => setRescheduleData(prev => ({ ...prev, confirmed_time: e.target.value }))}
                          className="w-full h-10 bg-black border border-white/20 text-white rounded-xl px-3"
                          required
                          data-testid="select-new-time"
                        >
                          <option value="">Select time</option>
                          {timeSlots.map((slot) => (
                            <option key={slot.value} value={slot.value}>{slot.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white font-sans">Stylist</Label>
                      <select
                        value={rescheduleData.confirmed_stylist || appointment?.stylist}
                        onChange={(e) => setRescheduleData(prev => ({ ...prev, confirmed_stylist: e.target.value }))}
                        className="w-full h-10 bg-black border border-white/20 text-white rounded-xl px-3"
                        data-testid="select-stylist"
                      >
                        {stylists.map((stylist) => (
                          <option key={stylist} value={stylist}>{stylist}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white font-sans">Note to Customer (optional)</Label>
                      <Textarea
                        value={rescheduleData.notes}
                        onChange={(e) => setRescheduleData(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="e.g., The requested time was unavailable..."
                        className="bg-black border-white/20 text-white placeholder:text-white/40 rounded-xl resize-none"
                        rows={2}
                        data-testid="input-notes"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowReschedule(false)}
                        className="flex-1 border-white/30 text-white hover:bg-white/10 font-sans font-medium rounded-full"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-[2] bg-isalonuk-accent hover:bg-isalonuk-accent-hover text-black font-sans font-semibold rounded-full"
                        data-testid="btn-submit-reschedule"
                      >
                        {isSubmitting ? (
                          <Loader2 size={18} className="mr-2 animate-spin" />
                        ) : (
                          'Confirm New Time'
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {confirmed && (
          <Card className="bg-isalonuk-accent/20 border-isalonuk-accent/30">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-isalonuk-accent rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-black" />
              </div>
              <h3 className="font-heading text-xl text-white mb-2">All Done!</h3>
              <p className="text-white/70 font-sans">
                The customer has been sent a confirmation email with all the details.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Staff Calendar Links */}
        {!cancelled && (
          <Card className="bg-zinc-900 border-white/10 mt-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar size={20} className="text-isalonuk-accent" />
                  <span className="text-white font-sans text-sm">Staff Calendars</span>
                </div>
                <div className="flex items-center gap-2">
                  {appointment?.stylist && (
                    <Link to={`/calendar/${encodeURIComponent(appointment.stylist)}`}>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-white/20 text-white hover:bg-white/10 text-xs"
                        data-testid="btn-stylist-calendar"
                      >
                        {appointment.stylist}'s Calendar
                      </Button>
                    </Link>
                  )}
                  <Link to="/calendar">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-isalonuk-accent/50 text-isalonuk-accent hover:bg-isalonuk-accent/10 text-xs"
                      data-testid="btn-all-calendars"
                    >
                      View All
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8">
          <Link to="/" className="text-isalonuk-accent font-sans text-sm hover:underline">
            ← Back to iSalon Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ConfirmAppointmentPage;
